import {
  Component, Inject, Injectable, PLATFORM_ID, computed, inject, input, output, signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { I18nService } from './i18n/i18n.service';
import { PLAY_STORE_URL, isAndroidBrowser, isIosDevice } from './app-store-links';

/** The non-standard `beforeinstallprompt` event — Chromium only, and not in lib.dom. */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Shared state for "install this app", so the button and the iOS help sheet can live in DIFFERENT
 * parts of the layout and still act as one feature.
 *
 * They have to be separated. The sheet is a `position: fixed` overlay, and both places the button
 * sits are stacking contexts: the header is `position: sticky; z-index: 50`, and the mobile drawer is
 * `transform: translateX(...)` — which for a fixed child is worse still, because a transformed
 * ancestor becomes its containing block, so the "full screen" overlay would be squeezed into a 300px
 * drawer. Rendering the sheet at the layout root keeps it in the root stacking context, where it
 * covers the page as intended.
 */
@Injectable({ providedIn: 'root' })
export class InstallAppService {
  private readonly isBrowser: boolean;

  /** A prompt is available (Chromium captured beforeinstallprompt). */
  readonly promptReady = signal(false);
  /** Already running as an installed app — nothing to offer. */
  readonly installed = signal(false);
  readonly isIos = signal(false);
  readonly busy = signal(false);
  /** Which help sheet is open, if any: iOS Share-menu steps, or the manual browser-menu route. */
  readonly helpMode = signal<'ios' | 'manual' | null>(null);
  readonly showIosHelp = computed(() => this.helpMode() !== null);

  /**
   * Show the button whenever we are NOT already inside the installed app.
   *
   * Deliberately NOT gated on `promptReady`: Chromium fires `beforeinstallprompt` only once its
   * criteria are met, and never at all on Firefox or on Chrome/Firefox for iOS — so gating on it hid
   * the button on exactly the devices where someone goes looking for it, with no explanation. (The POS
   * learned this the same way; see canOfferInstall there.) The button now always shows outside the
   * installed window and explains itself on tap.
   */
  readonly canOffer = computed(() => !this.installed());

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (!this.isBrowser) return;

    this.installed.set(this.isStandalone());
    this.isIos.set(this.detectIosSafari());
    // index.html may have captured the event long before Angular booted.
    this.promptReady.set(!!(window as any).__deferredInstallPrompt);

    // Root-provided and never destroyed for the app's lifetime, so these are not removed.
    window.addEventListener('pwa-installable', () => this.promptReady.set(true));
    window.addEventListener('pwa-installed', () => {
      this.installed.set(true);
      this.promptReady.set(false);
      this.helpMode.set(null);
    });
  }

  /**
   * iOS gets Share-menu instructions; a browser offering a prompt gets the real thing; anything else
   * gets the manual route rather than a button that appears to do nothing.
   */
  async activate(): Promise<void> {
    if (this.isIos()) { this.helpMode.set('ios'); return; }
    if (!this.promptReady()) { this.helpMode.set('manual'); return; }
    await this.install();
  }

  closeIosHelp(): void { this.helpMode.set(null); }

  private async install(): Promise<void> {
    const evt = (window as any).__deferredInstallPrompt as InstallPromptEvent | null;
    if (!evt || this.busy()) return;
    this.busy.set(true);
    try {
      await evt.prompt();
      const choice = await evt.userChoice;
      // The event is single-use either way — a second prompt() throws.
      (window as any).__deferredInstallPrompt = null;
      this.promptReady.set(false);
      if (choice.outcome === 'accepted') this.installed.set(true);
    } catch {
      this.promptReady.set(false);
    } finally {
      this.busy.set(false);
    }
  }

  /** True when launched from the home screen rather than a browser tab. */
  private isStandalone(): boolean {
    try {
      return window.matchMedia('(display-mode: standalone)').matches
        || window.matchMedia('(display-mode: fullscreen)').matches
        || (navigator as any).standalone === true;   // iOS Safari's own flag
    } catch {
      return false;
    }
  }

  /** iOS (including iPadOS, which reports as Mac but has touch) on a WebKit browser. */
  private detectIosSafari(): boolean {
    try {
      const ua = navigator.userAgent;
      const iOS = /iPad|iPhone|iPod/.test(ua)
        || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);
      if (!iOS) return false;
      // Chrome/Firefox on iOS are WebKit too and also cannot install — the Share-sheet advice is wrong
      // for them, so only offer it in Safari proper.
      return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
    } catch {
      return false;
    }
  }
}

/**
 * "Install app" button — puts orderYaa on the shopper's home screen so it opens full screen, keeps
 * their cart and session, and can show order notifications like a native app.
 *
 * Renders nothing at all when there is nothing to offer: already installed, or a browser that can
 * neither prompt nor be talked through it (Chrome/Firefox on iOS, for instance, cannot install).
 *
 * TWO PLACEMENTS, because this header is busy: a pill on desktop, and a row in the mobile drawer
 * where the nav links already move. The pill hides itself under 860px — the same breakpoint the
 * layout uses to switch to the drawer — so only ever one of them is on screen.
 *
 * Pair it with ONE {@link InstallHelpSheetComponent} at the layout root.
 */
@Component({
  selector: 'app-install-app',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (svc.canOffer()) {
      <button
        type="button"
        [class]="variant() === 'drawer' ? 'ia-row' : 'ia-pill'"
        [disabled]="svc.busy()"
        (click)="onClick($event)"
        [attr.aria-label]="svc.isIos() ? i18n.t('install.ariaHow') : i18n.t('install.ariaInstall')">
        <span class="ia-ico" aria-hidden="true">📲</span>
        <span class="ia-label">{{ svc.busy() ? i18n.t('install.installing') : i18n.t('install.button') }}</span>
      </button>
    }
  `,
  styles: [`
    :host { display: contents; }

    /* Header placement — a translucent white pill on the red bar. */
    .ia-pill {
      display: inline-flex; align-items: center; gap: 0.4rem; flex: 0 0 auto;
      border: 1px solid rgba(255, 255, 255, 0.45); background: rgba(255, 255, 255, 0.12);
      border-radius: 999px; padding: 0.4rem 0.8rem;
      color: #fff; font: inherit; font-size: 0.85rem; font-weight: 700; cursor: pointer;
      white-space: nowrap;
    }
    .ia-pill:hover:not(:disabled) { background: rgba(255, 255, 255, 0.22); border-color: #fff; }
    .ia-pill:disabled { opacity: 0.6; cursor: default; }

    /* Drawer placement — a full-width row matching .oy-drawer__links a. */
    .ia-row {
      display: flex; align-items: center; gap: 0.7rem; width: 100%;
      padding: 0.75rem 0.6rem; border-radius: 10px;
      /* "start", not "left" — this app mirrors for Urdu. */
      border: none; background: transparent; cursor: pointer; text-align: start;
      font: inherit; font-size: 1rem; font-weight: 600; color: var(--color-text, #17120f);
    }
    .ia-row:hover:not(:disabled) { background: #faf7f7; }
    .ia-row:disabled { opacity: 0.6; cursor: default; }

    /* Same 1.5rem icon box as .oy-drawer__links a and the text-size / language rows, so all five
       drawer labels start on one line. Emoji glyph widths differ enough to be obvious without it. */
    .ia-ico { flex: 0 0 1.5rem; text-align: center; font-size: 1rem; line-height: 1; }

    /* Below 1080px the layout moves its nav into the drawer, and the header has no room to spare
       (burger + logo + location + cart). The drawer row takes over there.
       MUST match ordering-layout.css's header breakpoint, else the pill renders in both at once.
       Moved 860 → 1080 on 2026-09-22 with the layout. */
    @media (max-width: 1080px) {
      .ia-pill { display: none; }
    }
  `],
})
export class InstallAppComponent {
  /** Where this instance is rendered — only affects styling and which breakpoint hides it. */
  readonly variant = input<'header' | 'drawer'>('header');

  /** Raised once the button has done its job, so the drawer can close itself behind it. */
  readonly activated = output<void>();

  protected readonly svc = inject(InstallAppService);
  protected readonly i18n = inject(I18nService);

  onClick(event: MouseEvent): void {
    event.stopPropagation();
    void this.svc.activate().finally(() => this.activated.emit());
  }
}

/** Set once the shopper closes the prompt, so it never asks a second time. */
const PROMPT_DISMISS_KEY = 'orderyaa.customer.installDismissed';

/**
 * The FIRST-VISIT install prompt — the same idea the rider app has: a card that offers to put orderYaa
 * on the home screen, appearing on its own rather than waiting to be found.
 *
 * Separate from {@link InstallAppComponent}, which is the always-available header/drawer button and is
 * untouched by this. The two coexist on purpose: dismissing this card is not a refusal to ever install,
 * so the button stays exactly where it was for anyone who changes their mind.
 *
 * WHY IT NEEDS ITS OWN DISMISSED STATE rather than borrowing the service's: the service is shared with
 * the button, and the button must keep working after this card is closed. So "the shopper closed the
 * prompt" is state that belongs to the prompt alone, and it is persisted — an install nudge that
 * returns on every visit is an annoyance, not a nudge.
 *
 * Only offered when there is genuinely something to offer: never inside the installed app, and only
 * where a real prompt exists or on iOS, where Share-menu instructions are the honest alternative.
 * Unlike the button, this one IS gated on that — the button explains itself when tapped, but a card
 * that appears uninvited must not appear where it cannot deliver.
 */
@Component({
  selector: 'app-install-prompt',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (show()) {
      <div class="ip" role="region" [attr.aria-label]="i18n.t('install.ariaInstall')">
        <span class="ip__icon" aria-hidden="true">📲</span>
        <div class="ip__text">
          <strong class="ip__title">{{ i18n.t('install.promptTitle') }}</strong>
          <span class="ip__sub">
            {{ svc.isIos() ? i18n.t('install.iosLead') : i18n.t('install.promptSub') }}
          </span>
        </div>
        <div class="ip__actions">
          <button type="button" class="ip__btn" [disabled]="svc.busy()" (click)="activate()">
            {{ svc.busy() ? i18n.t('install.installing') : i18n.t('install.button') }}
          </button>
          <button type="button" class="ip__close" (click)="dismiss()"
                  [attr.aria-label]="i18n.t('install.notNow')">✕</button>
        </div>
      </div>
    }
  `,
  styles: [`
    .ip {
      display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;
      margin: 0 0 1rem; padding: 0.75rem 0.9rem;
      background: #fff; border: 1px solid #f1e7e7;
      /* A brand edge rather than a full red card: this sits above the shop list and must not compete
         with it. Logical property so it moves to the right under Urdu. */
      border-inline-start: 4px solid var(--color-primary, #FA0E0E);
      border-radius: 12px; box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
    }
    .ip__icon { font-size: 1.5rem; line-height: 1; flex: 0 0 auto; }
    .ip__text { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; flex: 1 1 220px; }
    .ip__title { font-size: 0.92rem; font-weight: 800; color: var(--color-text, #1b2a47); }
    .ip__sub { font-size: 0.78rem; color: var(--color-text-muted, #5e6e8c); }
    .ip__actions { display: flex; align-items: center; gap: 0.4rem; flex: 0 0 auto; }
    .ip__btn {
      border: none; cursor: pointer; font: inherit; font-weight: 800; font-size: 0.85rem;
      background: var(--color-primary, #FA0E0E); color: #fff;
      padding: 0.55rem 1.1rem; border-radius: 999px;
    }
    .ip__btn:disabled { opacity: 0.6; cursor: default; }
    .ip__close {
      border: none; background: transparent; cursor: pointer;
      color: var(--color-text-muted, #5e6e8c); font-size: 1rem; padding: 0.35rem 0.5rem; line-height: 1;
    }
  `],
})
export class InstallPromptComponent {
  protected readonly svc = inject(InstallAppService);
  protected readonly i18n = inject(I18nService);

  private readonly dismissed = signal(true);   // assumed closed until storage says otherwise

  /**
   * An Android browser, where the strip above the header already offers the real app — a better
   * answer than this card to the same question, so the card stands down and the shopper is never
   * asked to install two things at once.
   *
   * Android ONLY, deliberately. A desktop cannot run the Android app: what the popup offers there
   * is a QR to carry it to a phone, which is a different thing from installing this site on the
   * machine in front of them. Standing this card down on a desktop took that away and put nothing
   * in its place, so a desktop keeps it — as does iOS, which has no listing at all.
   */
  private readonly playStoreOffered = !!PLAY_STORE_URL && isAndroidBrowser() && !isIosDevice();

  protected readonly show = computed(() =>
    !this.dismissed() && !this.svc.installed() && !this.playStoreOffered
    && (this.svc.promptReady() || this.svc.isIos()));

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    if (!isPlatformBrowser(platformId)) return;
    // Reading throws in some privacy modes; a failure leaves it dismissed, which is the safe default
    // for something that appears uninvited.
    try { this.dismissed.set(localStorage.getItem(PROMPT_DISMISS_KEY) === '1'); }
    catch { /* keep it hidden */ }
  }

  protected activate(): void {
    // Closed either way: an accepted install makes it moot, and a cancelled one has still been asked.
    void this.svc.activate().finally(() => this.dismiss());
  }

  protected dismiss(): void {
    this.dismissed.set(true);
    try { localStorage.setItem(PROMPT_DISMISS_KEY, '1'); } catch { /* private mode */ }
  }
}

/**
 * The iOS "Add to Home Screen" instructions. iOS Safari has no programmatic install, so this is the
 * only honest thing to offer there.
 *
 * Render this ONCE, at the layout root — never inside the header or the drawer. See the note on
 * {@link InstallAppService} for why: both of those are stacking contexts, and the drawer would also
 * become this overlay's containing block.
 */
@Component({
  selector: 'app-install-help-sheet',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (svc.showIosHelp()) {
      <div class="ia-sheet" role="dialog" aria-modal="true" aria-labelledby="ia-help-title"
           (click)="svc.closeIosHelp()">
        <div class="ia-sheet__card" (click)="$event.stopPropagation()">
          <div class="ia-sheet__head">
            <strong class="ia-sheet__title" id="ia-help-title">{{ i18n.t('install.sheetTitle') }}</strong>
            <button type="button" class="ia-sheet__x" (click)="svc.closeIosHelp()"
                    [attr.aria-label]="i18n.t('common.close')">✕</button>
          </div>
          @if (svc.helpMode() === 'ios') {
            <p class="ia-sheet__lead">{{ i18n.t('install.iosLead') }}</p>
            <ol class="ia-sheet__steps">
              <li>{{ i18n.t('install.iosStep1') }}</li>
              <li>{{ i18n.t('install.iosStep2') }}</li>
              <li>{{ i18n.t('install.iosStep3') }}</li>
            </ol>
          } @else {
            <!-- Reached when the browser isn't offering a one-click install: Firefox, an already-
                 installed copy, or Chromium before its criteria are met. Naming the menu is more use
                 than a dead button. -->
            <p class="ia-sheet__lead">{{ i18n.t('install.manualLead') }}</p>
            <ol class="ia-sheet__steps">
              <li>{{ i18n.t('install.manualChrome') }}</li>
              <li>{{ i18n.t('install.manualFirefox') }}</li>
              <li>{{ i18n.t('install.manualAlready') }}</li>
            </ol>
          }
          <p class="ia-sheet__note">{{ i18n.t('install.note') }}</p>
        </div>
      </div>
    }
  `,
  styles: [`
    :host { display: contents; }

    .ia-sheet {
      position: fixed; inset: 0; z-index: 1200;
      display: flex; align-items: flex-end; justify-content: center;
      background: rgba(0, 0, 0, 0.45); padding: 1rem;
    }
    .ia-sheet__card {
      width: 100%; max-width: 420px; background: #fff; border-radius: 16px;
      padding: 1rem 1.1rem 1.2rem; box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
      color: var(--color-text, #17120f);
    }
    .ia-sheet__head { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; }
    .ia-sheet__title { font-size: 1rem; font-weight: 800; }
    .ia-sheet__x {
      border: none; background: transparent; cursor: pointer; line-height: 1;
      color: var(--color-text-muted, #6b6b6b); font-size: 1rem; padding: 0.25rem 0.35rem;
    }
    .ia-sheet__lead { margin: 0.5rem 0 0; font-size: 0.88rem; color: var(--color-text-muted, #6b6b6b); }
    .ia-sheet__steps { margin: 0.75rem 0 0; padding-left: 1.15rem; display: grid; gap: 0.4rem; font-size: 0.9rem; }
    .ia-sheet__note { margin: 0.85rem 0 0; font-size: 0.8rem; color: var(--color-text-muted, #6b6b6b); }

    @media (min-width: 560px) {
      .ia-sheet { align-items: center; }
    }
  `],
})
export class InstallHelpSheetComponent {
  protected readonly svc = inject(InstallAppService);
  protected readonly i18n = inject(I18nService);
}
